import { useState, useEffect } from "react";
import { Form } from "react-bootstrap";
import "../HistoryAdequacao/History.css";

export interface HistoryInitialValue {
  status?: string;
  observacao?: string;
  solicitadoEm?: string;
  obsAnalista?: string;
  adequadoEm?: string;
  obsSindicato?: string;
  adequacaoRealizada?: boolean;
}

export interface HistoryProps {
  idCampo: string;
  initialValue?: HistoryInitialValue;
  onChange?: (idCampo: string, estado: Record<string, unknown>) => void;
  erro?: boolean;
  isSindicato?: boolean;
}

const History = ({
  idCampo,
  initialValue = {},
  onChange,
  erro = false,
  isSindicato = false,
}: HistoryProps) => {
  const iv = initialValue as HistoryInitialValue;
  const [status, setStatus] = useState("");
  const [observacao, setObservacao] = useState("");
  const [solicitadoEm, setSolicitadoEm] = useState("");
  const [obsAnalista, setObsAnalista] = useState("");
  const [adequadoEm, setAdequadoEm] = useState("");
  const [obsSindicato, setObsSindicato] = useState("");
  const [adequadoConformeSolicitado, setAdequadoConformeSolicitado] =
    useState(false);

  const isConforme =
    iv.status === "CONFORME" && iv.adequacaoRealizada === true;

  const formatToDatetimeLocal = (dateString) => {
    if (!dateString) return "";

    if (dateString.length === 10) {
      dateString = dateString + "T00:00";
    }

    dateString = dateString.replace(" ", "T");
    const d = new Date(dateString);

    d.setHours(d.getHours() - 3);

    const pad = (n) => n.toString().padStart(2, "0");
    const year = d.getFullYear();
    const month = pad(d.getMonth() + 1);
    const day = pad(d.getDate());
    const hour = pad(d.getHours());
    const minute = pad(d.getMinutes());

    const formattedDate = `${year}-${month}-${day}T${hour}:${minute}`;

    return formattedDate;
  };

  useEffect(() => {
    setStatus(iv.status || "");
    setObservacao(iv.observacao || "");
    setSolicitadoEm(formatToDatetimeLocal(iv.solicitadoEm));
    setObsAnalista(iv.obsAnalista || "");
    setAdequadoEm(formatToDatetimeLocal(iv.adequadoEm));
    setObsSindicato(iv.obsSindicato || "");
    setAdequadoConformeSolicitado(!!iv.adequacaoRealizada);
  }, [initialValue]);

  const handleAdequacaoChange = (field, value) => {
    let novoEstado = {
      status,
      observacao,
      solicitadoEm,
      obsAnalista,
      adequadoEm,
      obsSindicato,
      adequacaoRealizada: adequadoConformeSolicitado,
    };

    if (field === "status") {
      setStatus(value);
      novoEstado.status = value;
      if (onChange) onChange(idCampo, novoEstado);
      return;
    }

    if (field === "observacao") {
      setObservacao(value);
      novoEstado.observacao = value;
    }

    if (field === "obsAnalista") {
      setObsAnalista(value);
      novoEstado.obsAnalista = value;
    }

    if (field === "adequadoEm") {
      setAdequadoEm(value);
      novoEstado.adequadoEm = value;
    }

    if (field === "obsSindicato") {
      setObsSindicato(value);
      novoEstado.obsSindicato = value;
    }

    if (field === "adequacaoRealizada") {
      setAdequadoConformeSolicitado(value);
      novoEstado.adequacaoRealizada = value;
      if (value) {
        setStatus("CONFORME");
        novoEstado.status = "CONFORME";
      }
    }

    if (onChange) onChange(idCampo, novoEstado);
  };

  const adequacaoSolicitada = Boolean(solicitadoEm && solicitadoEm !== "");
  const aguardandoValidacaoAnalista = Boolean(
    isSindicato &&
    status === "NAO_CONFORME" &&
    adequadoConformeSolicitado &&
    solicitadoEm &&
    adequadoEm,
  );

  return (
    <div
      className={`mt-4 border pt-3 p-3 rounded ${isSindicato ? "bg-warning-subtle border-warning" : "bg-light"}`}
    >
      <h5 className="fw-bold">ANÁLISE</h5>

      {isSindicato &&
        status === "NAO_CONFORME" &&
        adequadoConformeSolicitado &&
        solicitadoEm &&
        adequadoEm && (
          <div
            className="alert alert-info mt-3 border-info text-dark"
            role="alert"
          >
            Esta adequação foi realizada pelo sindicato e está aguardando a
            validação do analista.
          </div>
        )}

      {isSindicato &&
        adequadoConformeSolicitado &&
        adequadoEm &&
        status === "CONFORME" && (
          <div className="alert alert-success mt-3" role="alert">
            Adequação conforme validada pelo analista.
          </div>
        )}

      {isSindicato &&
        status === "NAO_CONFORME" &&
        solicitadoEm &&
        !adequadoConformeSolicitado && (
          <div className="alert alert-danger mt-3" role="alert">
            Realizar Adequação.
          </div>
        )}

      <Form.Group className="mt-3">
        <Form.Label className="fw-semibold">Situação</Form.Label>
        <div
          className={`d-flex gap-4 ${erro ? "border border-danger p-2 rounded" : ""}`}
        >
          <Form.Check
            type="radio"
            label="CONFORME"
            checked={status === "CONFORME"}
            onChange={() => handleAdequacaoChange("status", "CONFORME")}
            disabled={adequacaoSolicitada}
          />
          <Form.Check
            type="radio"
            label="NÃO CONFORME"
            checked={status === "NAO_CONFORME"}
            onChange={() => handleAdequacaoChange("status", "NAO_CONFORME")}
            disabled={adequacaoSolicitada}
          />
        </div>
      </Form.Group>

      <div
        className={`p-3 mt-4 rounded border ${isSindicato ? "bg-warning-subtle border-warning" : "bg-light"}`}
      >
        <h6 className="fw-bold mb-3">SOLICITADO ADEQUAÇÃO</h6>

        <Form.Group className="mb-3">
          <Form.Label>Solicitado em:</Form.Label>
          <Form.Control type="datetime-local" value={solicitadoEm} disabled />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Observação analista:</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            maxLength={10000}
            value={obsAnalista}
            onChange={(e) =>
              handleAdequacaoChange("obsAnalista", e.target.value)
            }
            disabled={true}
          />
        </Form.Group>

        {adequadoEm && (
          <Form.Group className="mb-3">
            <Form.Label>Adequado em:</Form.Label>
            <Form.Control
              type="datetime-local"
              value={adequadoEm}
              onChange={(e) =>
                handleAdequacaoChange("adequadoEm", e.target.value)
              }
              disabled={adequacaoSolicitada}
            />
          </Form.Group>
        )}

        <Form.Group className="mt-3">
          <Form.Label>Observação sindicato:</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            maxLength={10000}
            value={obsSindicato}
            onChange={(e) =>
              handleAdequacaoChange("obsSindicato", e.target.value)
            }
            disabled={
              Boolean(isConforme || aguardandoValidacaoAnalista || isSindicato === false)
            }
          />
          <div className="text-muted text-end small">
            Máximo 10.000 caracteres
          </div>
        </Form.Group>

        {isSindicato && solicitadoEm && (
          <Form.Group className="mt-3">
            <Form.Check
              type="checkbox"
              label="ADEQUAÇÃO REALIZADA CONFORME O SOLICITADO"
              checked={adequadoConformeSolicitado}
              onChange={(e) =>
                handleAdequacaoChange("adequacaoRealizada", e.target.checked)
              }
              disabled={Boolean(isConforme || aguardandoValidacaoAnalista)}
            />
          </Form.Group>
        )}
      </div>
    </div>
  );
};

export default History;
